import { useState } from "react";
import { useNav } from "../context/NavContext";

const T = {
  bg:          "#0a1628",
  cardBg:      "rgba(255,255,255,0.05)",
  cardBorder:  "rgba(255,255,255,0.09)",
  inputBg:     "rgba(255,255,255,0.07)",
  inputBorder: "rgba(255,255,255,0.13)",
  cyan:        "#0ea5e9",
  cyanDim:     "rgba(14,165,233,0.15)",
  cyanBorder:  "rgba(14,165,233,0.28)",
  textPrimary: "#f0f9ff",
  textSub:     "#7dd3fc",
  textMuted:   "rgba(255,255,255,0.38)",
  textLabel:   "rgba(255,255,255,0.70)",
  error:       "#f87171",
};

const LOAN_PURPOSES = [
  "Debt Consolidation","Home Renovation","Medical Expenses",
  "Wedding Expenses","Travel & Vacation","Emergency Expenses","Buying Gadgets","Others",
];

const SALARY_MODES = ["Bank", "Cash", "Cheque"];
const EMP_TYPES    = ["Salaried", "Self Employed"];

// ── Helpers ───────────────────────────────────────────────────────────────────

function Label({ children }) {
  return <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: T.textLabel, marginBottom: 6, letterSpacing: "0.04em" }}>{children}</label>;
}

function Err({ msg }) {
  if (!msg) return null;
  return <p style={{ fontSize: 11, color: T.error, marginTop: 4 }}>{msg}</p>;
}

function Card({ children }) {
  return (
    <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 20, padding: "20px 18px", backdropFilter: "blur(12px)", marginBottom: 14 }}>
      {children}
    </div>
  );
}

function SectionHeader({ emoji, title }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(14,165,233,0.18)", border: "1px solid rgba(14,165,233,0.28)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{emoji}</div>
      <span style={{ fontSize: 17, fontWeight: 700, color: T.textPrimary }}>{title}</span>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function LoanDetailsScreen() {
  const { navigate } = useNav();

  const [form, setForm] = useState({
    purpose: "", loanAmount: "", email: "",
    aadhaarMobile: "", altMobile: "",
    pincode: "", city: "", state: "",
    employmentType: "", monthlyIncome: "",
    salaryMode: "", companyName: "", salaryDate: "",
    terms: false,
  });
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  const inputStyle = (name, hasErr) => ({
    width: "100%", padding: "13px 14px", borderRadius: 12,
    border: `1px solid ${hasErr ? T.error : focused === name ? T.cyan : T.inputBorder}`,
    background: T.inputBg, color: T.textPrimary, fontSize: 14, outline: "none",
    boxSizing: "border-box", transition: "border-color 0.2s",
    boxShadow: focused === name ? `0 0 0 3px ${T.cyanDim}` : "none",
  });

  const SegBtn = ({ label, active, field, value, hasErr }) => (
    <button type="button" onClick={() => set(field)(value)} style={{
      flex: 1, padding: "13px 0", borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer",
      border: `1px solid ${active ? T.cyan : hasErr ? T.error : "rgba(255,255,255,0.12)"}`,
      background: active ? T.cyanDim : "rgba(255,255,255,0.06)",
      color: active ? T.cyan : T.textMuted, transition: "all 0.18s",
    }}>{label}</button>
  );

  const validate = () => {
    const e = {};
    if (!form.purpose)       e.purpose       = "Select a loan purpose";
    if (!form.loanAmount)    e.loanAmount    = "Enter loan amount";
    if (!form.email || !form.email.includes("@")) e.email = "Enter a valid email";
    if (!form.aadhaarMobile || form.aadhaarMobile.length !== 10) e.aadhaarMobile = "Enter valid 10-digit number";
    if (!form.altMobile || form.altMobile.length !== 10) e.altMobile = "Enter valid 10-digit number";
    if (!form.pincode || form.pincode.length !== 6) e.pincode = "Enter valid 6-digit pincode";
    if (!form.city)          e.city          = "Enter city";
    if (!form.state)         e.state         = "Enter state";
    if (!form.employmentType) e.employmentType = "Select employment type";
    if (!form.monthlyIncome) e.monthlyIncome = "Enter monthly income";
    if (!form.salaryMode)    e.salaryMode    = "Select salary mode";
    if (!form.companyName)   e.companyName   = "Enter company name";
    if (!form.salaryDate)    e.salaryDate    = "Select salary date";
    if (!form.terms)         e.terms         = "Accept terms to continue";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowErrors(true);
    if (!validate()) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate("rejected");
    }, 1000);
  };

  const e = (key) => showErrors && errors[key];

  return (
    <div style={{ minHeight: "100dvh", background: T.bg, position: "relative", overflow: "hidden", paddingBottom: 120 }}>
      {/* Orbs */}
      <div style={{ position: "absolute", top: -80, left: -60, width: 260, height: 260, borderRadius: "50%", background: "#0ea5e9", opacity: 0.08, filter: "blur(70px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: 120, right: -60, width: 200, height: 200, borderRadius: "50%", background: "#38bdf8", opacity: 0.06, filter: "blur(60px)", pointerEvents: "none" }} />

      <div style={{ position: "relative", zIndex: 1, padding: "0 20px", maxWidth: 480, margin: "0 auto" }}>

        {/* Progress */}
        <div style={{ paddingTop: 20, marginBottom: 20 }}>
          <div style={{ height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: "20%", height: "100%", background: T.cyan, borderRadius: 4 }} />
          </div>
        </div>

        {/* Page header */}
        <div style={{ marginBottom: 24 }}>
          <span style={{ display: "inline-block", background: T.cyanDim, border: `1px solid ${T.cyanBorder}`, borderRadius: 20, padding: "4px 12px", fontSize: 11, color: T.textSub, marginBottom: 10 }}>
            📋 Step 1 of 5
          </span>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: T.textPrimary, marginBottom: 4 }}>Loan Details</h1>
          <p style={{ fontSize: 13, color: T.textMuted }}>Tell us about your requirement and employment</p>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off">

          {/* ── LOAN DETAILS ── */}
          <Card>
            <SectionHeader emoji="💰" title="Loan Details" />

            <div style={{ marginBottom: 16 }}>
              <Label>Why do you need this loan?</Label>
              <div style={{ position: "relative" }}>
                <select value={form.purpose} onChange={(ev) => set("purpose")(ev.target.value)}
                  style={{ ...inputStyle("purpose", e("purpose")), appearance: "none", cursor: "pointer" }}>
                  <option value="" style={{ background: "#0d1b2e" }}>Select Loan Purpose</option>
                  {LOAN_PURPOSES.map((p) => <option key={p} value={p} style={{ background: "#0d1b2e" }}>{p}</option>)}
                </select>
                <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: T.textMuted, pointerEvents: "none", fontSize: 12 }}>▼</span>
              </div>
              <Err msg={e("purpose")} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Label>Loan Amount Required</Label>
              <input value={form.loanAmount} onChange={(ev) => set("loanAmount")(ev.target.value)}
                placeholder="e.g. 50000" onFocus={() => setFocused("loanAmount")} onBlur={() => setFocused(null)}
                style={inputStyle("loanAmount", e("loanAmount"))} />
              <Err msg={e("loanAmount")} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Label>Email Address</Label>
              <input type="email" value={form.email} onChange={(ev) => set("email")(ev.target.value)}
                placeholder="your@email.com" onFocus={() => setFocused("email")} onBlur={() => setFocused(null)}
                style={inputStyle("email", e("email"))} />
              <Err msg={e("email")} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Label>Aadhaar Linked Mobile</Label>
              <input type="tel" maxLength={10} value={form.aadhaarMobile}
                onChange={(ev) => set("aadhaarMobile")(ev.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="10-digit mobile" onFocus={() => setFocused("aadhaarMobile")} onBlur={() => setFocused(null)}
                style={inputStyle("aadhaarMobile", e("aadhaarMobile"))} />
              <Err msg={e("aadhaarMobile")} />
            </div>

            <div>
              <Label>Alternate Mobile</Label>
              <input type="tel" maxLength={10} value={form.altMobile}
                onChange={(ev) => set("altMobile")(ev.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="10-digit alternate number" onFocus={() => setFocused("altMobile")} onBlur={() => setFocused(null)}
                style={inputStyle("altMobile", e("altMobile"))} />
              <Err msg={e("altMobile")} />
            </div>
          </Card>

          {/* ── LOCATION ── */}
          <Card>
            <SectionHeader emoji="📍" title="Location Details" />

            <div style={{ marginBottom: 16 }}>
              <Label>Pincode</Label>
              <input maxLength={6} value={form.pincode}
                onChange={(ev) => set("pincode")(ev.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit pincode" onFocus={() => setFocused("pincode")} onBlur={() => setFocused(null)}
                style={inputStyle("pincode", e("pincode"))} />
              <Err msg={e("pincode")} />
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <Label>City</Label>
                <input value={form.city} onChange={(ev) => set("city")(ev.target.value)}
                  placeholder="City" onFocus={() => setFocused("city")} onBlur={() => setFocused(null)}
                  style={inputStyle("city", e("city"))} />
                <Err msg={e("city")} />
              </div>
              <div style={{ flex: 1 }}>
                <Label>State</Label>
                <input value={form.state} onChange={(ev) => set("state")(ev.target.value)}
                  placeholder="State" onFocus={() => setFocused("state")} onBlur={() => setFocused(null)}
                  style={inputStyle("state", e("state"))} />
                <Err msg={e("state")} />
              </div>
            </div>
          </Card>

          {/* ── EMPLOYMENT ── */}
          <Card>
            <SectionHeader emoji="💼" title="Employment Info" />

            <div style={{ marginBottom: 16 }}>
              <Label>Employment Type</Label>
              <div style={{ display: "flex", gap: 10 }}>
                {EMP_TYPES.map((t) => (
                  <SegBtn key={t} label={t} active={form.employmentType === t}
                    field="employmentType" value={t} hasErr={!!e("employmentType")} />
                ))}
              </div>
              <Err msg={e("employmentType")} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Label>Monthly Income</Label>
              <input value={form.monthlyIncome} onChange={(ev) => set("monthlyIncome")(ev.target.value)}
                placeholder="e.g. 30000" onFocus={() => setFocused("income")} onBlur={() => setFocused(null)}
                style={inputStyle("income", e("monthlyIncome"))} />
              <Err msg={e("monthlyIncome")} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Label>Salary Received In</Label>
              <div style={{ display: "flex", gap: 10 }}>
                {SALARY_MODES.map((m) => (
                  <SegBtn key={m} label={m} active={form.salaryMode === m}
                    field="salaryMode" value={m} hasErr={!!e("salaryMode")} />
                ))}
              </div>
              <Err msg={e("salaryMode")} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Label>Company Name</Label>
              <input value={form.companyName} onChange={(ev) => set("companyName")(ev.target.value)}
                placeholder="Enter company name" onFocus={() => setFocused("company")} onBlur={() => setFocused(null)}
                style={inputStyle("company", e("companyName"))} />
              <Err msg={e("companyName")} />
            </div>

            <div>
              <Label>Salary Date (day of month)</Label>
              <div style={{ position: "relative" }}>
                <select value={form.salaryDate} onChange={(ev) => set("salaryDate")(ev.target.value)}
                  style={{ ...inputStyle("salaryDate", e("salaryDate")), appearance: "none", cursor: "pointer" }}>
                  <option value="" style={{ background: "#0d1b2e" }}>Select date</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d} style={{ background: "#0d1b2e" }}>{d}</option>
                  ))}
                </select>
                <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: T.textMuted, pointerEvents: "none", fontSize: 12 }}>▼</span>
              </div>
              <Err msg={e("salaryDate")} />
            </div>
          </Card>

          {/* ── TERMS ── */}
          <div style={{
            background: T.cardBg,
            border: `1px solid ${showErrors && errors.terms ? T.error : T.cardBorder}`,
            borderRadius: 16, padding: "16px 18px", backdropFilter: "blur(12px)", marginBottom: 14,
          }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}>
              <div onClick={() => set("terms")(!form.terms)} style={{
                width: 20, height: 20, borderRadius: 6, flexShrink: 0, marginTop: 1,
                border: `1.5px solid ${form.terms ? T.cyan : T.inputBorder}`,
                background: form.terms ? T.cyanDim : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", transition: "all 0.18s",
              }}>
                {form.terms && (
                  <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                    <path d="M1 4L4 7.5L10 1" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <span style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.7 }}>
                I agree to the{" "}
                <span style={{ color: T.cyan, textDecoration: "underline" }}>Terms & Conditions</span>
                {" "}and authorize the processing of my loan application.
              </span>
            </label>
            <Err msg={e("terms")} />
          </div>

          {/* ── Submit ── */}
          <button type="submit" disabled={loading} style={{
            width: "100%", padding: "16px", border: "none", borderRadius: 14,
            background: loading ? "rgba(14,165,233,0.25)" : "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
            color: loading ? "rgba(255,255,255,0.4)" : "#fff",
            fontSize: 15, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer",
            boxShadow: loading ? "none" : "0 4px 24px rgba(14,165,233,0.40)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          }}>
            {loading ? (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: "spin 0.8s linear infinite" }}>
                  <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.2)" strokeWidth="2.5" />
                  <path d="M8 2a6 6 0 0 1 6 6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
                Submitting…
              </>
            ) : "Submit Application →"}
          </button>
        </form>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        select option { background: #0d1b2e !important; color: #f0f9ff !important; }
        input::placeholder { color: rgba(255,255,255,0.28) !important; }
      `}</style>
    </div>
  );
}
